import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker/dist/react-datepicker.min.js';
import useTranslate from 'hooks/useTranslate';
import useUserStore from 'store/user';
import { useUpdateUserDetailsMutation } from 'services';
import { EducationInterface } from 'constants/interfaces';
import { educationInitialValues, educationSchema } from 'constants/schemaValidations';
import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import useCommonStore from 'store/common';
import { getMinDate } from 'utils';

const AddEducation: React.FC<any> = () => {
	const { translate } = useTranslate();
	const { profileData }: any = useUserStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();
	const { education: savedData = [] } = profileData;
	const { modalConfig = {} }: any = useCommonStore((state) => state);

	const getLimitedFields = (data: any[]): EducationInterface[] => {
		return data.map((item) => ({
			universityName: item.universityName || '',
			degree: item.degree || '',
			graduationDate: item.graduationDate || '',
			graduationEndDate: item.graduationEndDate || '',
			currentlyStudying: item.currentlyStudying === true || false,
		}));
	};

	const formik: any = useFormik({
		initialValues: {
			...educationInitialValues,
			...(Number.isInteger(modalConfig?.id) && savedData[modalConfig.id]
				? savedData[modalConfig.id]
				: {}),
		},
		validationSchema: educationSchema(translate),
		enableReinitialize: true,
		onSubmit: (values: any) => {
			const limitedFieldsData: EducationInterface[] =
				savedData?.length > 0 ? getLimitedFields(savedData) : [];
			const newDatas = [...limitedFieldsData];
			if (Number.isInteger(modalConfig?.id)) {
				newDatas[modalConfig.id] = {
					...values,
					graduationDate:
						typeof formik.values?.graduationDate !== 'string'
							? formik.values?.graduationDate?.getFullYear().toString()
							: formik.values?.graduationDate,
					graduationEndDate: values?.currentlyStudying
						? ''
						: typeof formik.values?.graduationEndDate !== 'string'
							? formik.values?.graduationEndDate?.getFullYear().toString()
							: formik.values?.graduationEndDate,
				};
			} else {
				newDatas.push({
					...values,
					graduationDate:
						typeof formik.values?.graduationDate !== 'string'
							? formik.values?.graduationDate?.getFullYear().toString()
							: formik.values?.graduationDate,
					graduationEndDate: values?.currentlyStudying
						? ''
						: typeof formik.values?.graduationEndDate !== 'string'
							? formik.values?.graduationEndDate?.getFullYear().toString()
							: formik.values?.graduationEndDate,
				});
			}
			updateProfile({ education: newDatas, saveType: 'addEducation' })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});

	const renderError = <T extends keyof EducationInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.addEducation')}</h4>
					<Row>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.universityName')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterUniversityName')}
									className="rounded-pill"
									name="universityName"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.universityName}
									autoComplete="off"
								/>
								{renderError('universityName')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.degreeName')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterDegreeName')}
									className="rounded-pill"
									name="degree"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.degree}
									autoComplete="off"
								/>
								{renderError('degree')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Common.duration')} ({translate('Common.from')})
								</Form.Label>
								<DatePicker
									selected={formik.values.graduationDate}
									onChange={(date: any) => formik.setFieldValue('graduationDate', date)}
									name="graduationDate"
									onBlur={formik.handleBlur}
									dateFormat="yyyy"
									showYearPicker
									minDate={getMinDate()}
									placeholderText={`${translate('PlaceHolder.onlyYear')}`}
									maxDate={new Date()}
								/>
								{renderError('graduationDate')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Common.duration')} ({translate('Common.to')})
								</Form.Label>
								<Form.Check
									type="switch"
									// id="custom-switch"
									label={translate('PlaceHolder.currentlyPursuing')}
									className="switch-btn position-absolute top-0 end-0"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									checked={formik.values.currentlyStudying}
									autoComplete="off"
									name="currentlyStudying"
								/>
								{renderError('currentlyStudying')}
								<DatePicker
									selected={formik.values.graduationEndDate}
									onChange={(date: any) => {
										formik.setFieldValue('graduationEndDate', date);
									}}
									name="graduationEndDate"
									onBlur={formik.handleBlur}
									dateFormat="yyyy"
									showYearPicker
									minDate={getMinDate()}
									disabled={formik.values.currentlyStudying}
									placeholderText={`${translate('PlaceHolder.onlyYear')}`}
									maxDate={new Date()}
									autoComplete="off"
								/>
								{renderError('graduationEndDate')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default AddEducation;
